import {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useState,
} from 'react'
import {
  ICreatePatientRegistration,
  IHealthPlan,
  IHealthPlanList,
  IPatientRegistrationContext,
  IPreRegistration,
  IRegistrationHolder,
  IResponsePreRegistrationData,
} from '../patient-registration-types'
import ServiceRegistration from '@/modules/PatientRegistration/patient-registration-services'
import { toast } from '@conexasaude/hero'
import { stepsRoutes } from '../constants'
import { useHistory } from 'react-router-dom'

const PatientRegistrationContext =
  createContext<IPatientRegistrationContext>(null)

export const PatientRegistrationProvider: FunctionComponent = ({
  children,
}) => {
  const history = useHistory()

  const [userData, setUserData] = useState<IPreRegistration>(
    {} as IPreRegistration,
  )
  const [preRegistrationData, setPreRegistrationData] =
    useState<IResponsePreRegistrationData>({} as IResponsePreRegistrationData)
  const [healthPlanData, setHealthPlanData] = useState<IHealthPlan>(
    {} as IHealthPlan,
  )
  const [patientRegistrationData, setPatientRegistrationData] =
    useState<ICreatePatientRegistration>({} as ICreatePatientRegistration)
  const [messageErrorPatientRegistration, setMessageErrorPatientRegistration] =
    useState('')
  const [responseRegistrationHolder, setResponseRegistrationHolder] =
    useState<IRegistrationHolder>({} as IRegistrationHolder)
  const [isEditUpdateData, setIsEditUpdateData] = useState(false)

  const changeUserData = useCallback(
    (field: string, value: string | boolean) => {
      setUserData(prevState => ({
        ...prevState,
        [field]: value,
      }))
    },
    [],
  )

  const changeHealthPlanData = useCallback(
    (field: string, value: string | number) => {
      setHealthPlanData(prevState => ({
        ...prevState,
        [field]: value,
      }))
    },
    [],
  )

  const changePreRegistrationData = useCallback(
    (field: string, value: string) => {
      setPreRegistrationData(prevState => ({
        ...prevState,
        [field]: value,
      }))
    },
    [],
  )

  const resetPatientRegistration = useCallback(() => {
    setUserData({} as IPreRegistration)
    setPreRegistrationData({} as IResponsePreRegistrationData)
    setHealthPlanData({} as IHealthPlanList)
    setPatientRegistrationData({} as ICreatePatientRegistration)
    history.push(stepsRoutes.START)
  }, [history])

  const resetDataHealthPlan = useCallback(() => {
    setHealthPlanData({} as IHealthPlanList)
    changePreRegistrationData('carteirinha', null)
  }, [changePreRegistrationData])

  const createPreRegistration = useCallback(async () => {
    try {
      const response = await ServiceRegistration.createPreRegistration(userData)
      setPreRegistrationData(response)

      if (userData?.tipoEnvio === 'EMAIL') {
        history.push(stepsRoutes.CODE_VALIDATION_MAIL)
      } else {
        history.push(stepsRoutes.CODE_VALIDATION_SMS)
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.object?.msg ||
        err?.response?.data?.msg ||
        err.message

      toast(errorMessage, {
        variant: 'danger',
        autoClose: 5000,
      })
    }
  }, [history, userData])

  return (
    <PatientRegistrationContext.Provider
      value={{
        preRegistrationData,
        userData,
        healthPlanData,
        patientRegistrationData,
        messageErrorPatientRegistration,
        responseRegistrationHolder,
        isEditUpdateData,
        resetDataHealthPlan,
        setIsEditUpdateData,
        setPreRegistrationData,
        setHealthPlanData,
        setUserData,
        setPatientRegistrationData,
        setMessageErrorPatientRegistration,
        setResponseRegistrationHolder,
        changeUserData,
        createPreRegistration,
        changeHealthPlanData,
        resetPatientRegistration,
        changePreRegistrationData,
      }}
    >
      {children}
    </PatientRegistrationContext.Provider>
  )
}

export const usePatientRegistrationContext =
  (): IPatientRegistrationContext => {
    const context = useContext(PatientRegistrationContext)
    if (!context) {
      throw new Error(
        'usePatientRegistrationContext must be used within a PatientRegistrationProvider',
      )
    }
    return context
  }
